import { Button, Input, useDialog } from '@emailforge/ui';
import { useEffect, useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import { type TenantTokens,useGetTokensQuery, useUpdateTokensMutation } from '@/features/tokens/tokensApi';
import { t } from '@/lib/i18n';

export function TokensScreen() {
  const { data } = useGetTokensQuery();
  const [update, { isLoading: isSaving }] = useUpdateTokensMutation();
  const [tokens, setTokens] = useState<TenantTokens>({ colors: {}, fonts: {}, spacing: {} });
  const { prompt } = useDialog();

  useEffect(() => {
    if (data) setTokens(data);
  }, [data]);

  const patchColor = (k: string, v: string) =>
    setTokens({ ...tokens, colors: { ...(tokens.colors ?? {}), [k]: v } });
  const patchFont = (k: string, v: string) =>
    setTokens({ ...tokens, fonts: { ...(tokens.fonts ?? {}), [k]: v } });

  const colorKeys = Object.keys(tokens.colors ?? {});
  const fontKeys = Object.keys(tokens.fonts ?? {});

  const save = async () => {
    await update(tokens).unwrap();
  };

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), t('nav.tokens')]}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button variant="ghost" size="sm" onClick={() => {
                const blob = new Blob([JSON.stringify(tokens, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'tokens.json';
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Icon.arrowDown size={13} /> Export tokens.json
            </Button>
            <Button variant="flame" size="sm" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save tokens'}
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <div
            style={{
              padding: 14,
              background: 'var(--flame-soft)',
              border: '1px solid var(--flame-line)',
              color: 'var(--flame-2)',
              fontSize: 12.5,
              borderRadius: 'var(--r-3)',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon.bolt size={14} />
            <span>
              Change a token here and your next render applies it. Publish templates to propagate.
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="mf-card">
              <div className="mf-card-head">
                <span className="mf-card-title">Colors</span>
                <div style={{ flex: 1 }} />
                <Button variant="ghost" size="sm" onClick={async () => {
                    const k = await prompt({
                      title: 'Add color token',
                      description: 'Choose a name like "accent", "warning" or "muted". The hex/oklch value can be edited inline after creating.',
                      placeholder: 'e.g. accent',
                      confirmLabel: 'Add',
                    });
                    if (k) patchColor(k, 'oklch(0.6 0.15 45)');
                  }}
                >
                  <Icon.plus size={12} />
                </Button>
              </div>
              <div style={{ padding: 14, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {colorKeys.map((k) => (
                  <div key={k} className="mf-card" style={{ overflow: 'hidden' }}>
                    <div style={{ height: 56, background: tokens.colors?.[k] ?? 'transparent' }} />
                    <div style={{ padding: 10 }}>
                      <div
                        className="mono"
                        style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
                      >
                        {k}
                      </div>
                      <Input mono
                        
                        style={{ marginTop: 6, fontSize: 10.5 }}
                        value={tokens.colors?.[k] ?? ''}
                        onChange={(e) => patchColor(k, e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mf-card">
              <div className="mf-card-head">
                <span className="mf-card-title">Typography</span>
                <div style={{ flex: 1 }} />
                <Button variant="ghost" size="sm" onClick={async () => {
                    const k = await prompt({
                      title: 'Add font token',
                      description: 'Pick a slot like "heading", "body" or "mono". The CSS family stack can be edited inline after creating.',
                      placeholder: 'e.g. heading',
                      confirmLabel: 'Add',
                    });
                    if (k) patchFont(k, 'Geist, sans-serif');
                  }}
                >
                  <Icon.plus size={12} />
                </Button>
              </div>
              <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {fontKeys.map((k) => (
                  <div key={k}>
                    <div
                      className="mono"
                      style={{
                        fontSize: 10.5,
                        color: 'var(--ink-3)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.06,
                        marginBottom: 4,
                      }}
                    >
                      {k}
                    </div>
                    <Input mono
                      
                      value={tokens.fonts?.[k] ?? ''}
                      onChange={(e) => patchFont(k, e.target.value)} />
                    <div
                      style={{
                        padding: 10,
                        marginTop: 6,
                        background: 'var(--paper-2)',
                        borderRadius: 'var(--r-2)',
                        fontFamily: tokens.fonts?.[k] ?? 'inherit',
                        fontSize: 20,
                        letterSpacing: -0.01,
                      }}
                    >
                      The quick brown fox
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
